import { config } from "@config/config";
import { ExclamationTriangleIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { MEDICAL_NECESSITY_DSM5_CRITERIA } from "@utils/constants";
import { convertChecklistToString, convertStringToChecklist, isAllTimeSentinel, unwrapConnection } from "@utils/utils";
import ApexCharts from "apexcharts";
import { format } from "date-fns";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  Button,
  CalendarDateRange,
  CheckboxList,
  Label,
  Paragraph,
  SelectItem,
  Small,
  Tooltip,
} from "@fronterahealth/frontera-ui-components";

import {
  ApiReportGoalAiBaselineDataPredictionStatusChoices,
  ApiReportGoalAiGoalAttainmentScalePredictionStatusChoices,
  ApiReportGoalAiProgressSummaryPredictionStatusChoices,
  ApiReportGoalChartTypeChoices,
  ApiReportGoalGasMatchStatusChoices,
  ApiReportTemplateFileUploadConfigChoices,
  CatalightGoalStatus,
  ReportGoalInput,
  ReportGoalType,
  useReassessmentShortTermGoalPanelQuery,
  useReassignReportGoalMutation,
  useStartGoalAttainmentScaleWorkflowMutation,
  useStartGoalBaselineDataWorkflowMutation,
  useStartGoalProgressSummaryWorkflowMutation,
  useUpdateGoalProgressSummaryMutation,
} from "@api/graphql/types-and-hooks";
import { GasPanel } from "@components/GoalsSections/GasPanel";
import { InlineEditField } from "@components/ProgrammingReview/InlineEditField";
import { InlineEditFieldWithAI } from "@components/ProgrammingReview/InlineEditFieldWithAI";
import {
  ShortTermGoalProgressGraph,
  chartLabelOverrides,
} from "@components/ShortTermGoalPanel/ShortTermGoalProgressGraph";
import { CATALIGHT_GOAL_STATUS_SELECT_OPTIONS, statusMap } from "@components/ShortTermGoalPanel/catalightConstants";
import { getGoalFieldGenerationStatus } from "@components/ShortTermGoalPanel/helpers";
import { TreatmentPlanForm } from "@components/TreatmentPlanFieldForm/TreatmentPlanForm";
import { useInlineTreatmentPlanEdit } from "@components/TreatmentPlanFieldForm/useInlineTreatmentPlanEdit";
import { useTreatmentPlanTemplate } from "@components/TreatmentPlanFieldForm/useTreatmentPlanTemplate";
import { goalToFieldValues } from "@components/TreatmentPlanFieldForm/utils";
import { convertReadableString } from "@components/forms/utils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { ReportType, useReportAnalytics } from "@hooks/useReportAnalytics";
import { ApprovalActionBar } from "@pages/ClinicalReportDetails/ReportPage/Components/ApprovalActionBar";
import { ApprovalBanner } from "@pages/ClinicalReportDetails/ReportPage/Components/ApprovalBanner";
import { GoalBadge } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalBadge";
import { GoalDomainTag } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalDomainTag";
import { InlineEditorSkeleton } from "@pages/ClinicalReportDetails/ReportPage/Components/InlineEditorSkeleton";
import { getGoalApprovalErrors as getGoalApprovalErrorsForContext } from "@pages/ClinicalReportDetails/ReportPage/Components/goalApprovalValidation";
import { DATE_FORMAT } from "@pages/VideoAnalytics/helpers";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { useReport } from "@providers/ReportProvider";

interface GoalInlineEditorNavigation {
  onPreviousGoal?: () => void;
  onNextGoal?: () => void;
  onPreviousTarget?: () => void;
  onNextTarget?: () => void;
  /**
   * Called after a successful Approve to walk forward to the next UNAPPROVED
   * item in the goal/target stack (skipping anything already approved).
   * Receives the id of the item that was just approved so the handler can
   * detect a manual navigation during the toast window and skip the advance.
   */
  onAdvanceAfterApprove?: (fromItemId: string) => void;
}

interface GoalInlineEditorProps {
  goal: ReportGoalType;
  navigation?: GoalInlineEditorNavigation;
}

export const GoalInlineEditor: React.FC<GoalInlineEditorProps> = ({ goal, navigation }) => {
  const {
    isReportCompleted: readOnly,
    isAddTargetsEnabled,
    isReassessment,
    isInitialAssessment,
  } = useClinicalReportDetailsData();
  const { report } = useReport();
  let reportType: ReportType = "diagnosis_builder";
  if (isReassessment) reportType = "reassessment";
  else if (isInitialAssessment) reportType = "initial_assessment";
  const { captureGoalApproved, captureGoalApprovalRevoked, captureGoalApproveAttempted } = useReportAnalytics({
    report_id: report?.id ?? "",
    report_type: reportType,
    template_id: report?.reportTemplate?.id,
    template_name: report?.reportTemplate?.name,
    template_payor_type: report?.reportTemplate?.payorType ?? undefined,
    template_export_framework: report?.reportTemplate?.exportFramework ?? undefined,
  });
  // Tracks approve-button clicks per goal so attempts_before_success can be
  // computed on goal_approved and rage-click depth is visible on goal_approve_attempted.
  const approvalAttemptRef = useRef(0);
  useEffect(() => {
    approvalAttemptRef.current = 0;
  }, [goal.id]);
  // Toast `onClose` captures its closure once. Read the latest navigation
  // (which carries a fresh `onAdvanceAfterApprove` bound to current `selected`)
  // through a ref so a stale closure can't override a manual navigation that
  // happened during the toast window.
  const navigationRef = useRef(navigation);
  navigationRef.current = navigation;
  const learnerId = report?.learner?.id || "";
  const isCatalightTemplate =
    report?.reportTemplate?.fileUploadConfig === ApiReportTemplateFileUploadConfigChoices.Catalight.valueOf();
  const reportDomain = goal.reportDomain;

  const queryClient = useQueryClient();

  const currentDomainId = goal?.reportDomain?.id;

  const reassignDomainMutation = useReassignReportGoalMutation({
    onError: () => {
      notifyError("Failed to change goal domain");
    },
    onSuccess: async () => {
      await invalidateGoalQuery();
    },
  });

  const handleDomainChange = (newDomainId: string) => {
    reassignDomainMutation.mutate({ input: { reportGoalId: goal.id, newReportDomainId: newDomainId } });
  };

  const { goalFields, isGasEnabled, isMedicalNecessityEnabled } = useTreatmentPlanTemplate(report?.id);
  const [isLocalGasGenerating, setIsLocalGasGenerating] = useState(false);
  const [progressSummary, setProgressSummary] = useState<string | null>(null);
  const [filterSessionDates, setFilterSessionDates] = useState<CalendarDateRange | undefined>({});
  const chartRef = useRef<ApexCharts>(null);
  const [signedChartImageUrl, setSignedChartImageUrl] = useState<string>(goal.signedChartImageUrl || "");
  // Gates Approve until the chart confirms its mixed-type auto-switch has settled.
  const [selectedChartReady, setSelectedChartReady] = useState(false);
  const [selectedChart, setSelectedChart] = useState<SelectItem>({
    id: goal.chartType as ApiReportGoalChartTypeChoices,
    primary: convertReadableString(
      chartLabelOverrides[(goal.chartType as ApiReportGoalChartTypeChoices) ?? ApiReportGoalChartTypeChoices.Progress],
    ),
  });

  const goalPanelQuery = useReassessmentShortTermGoalPanelQuery(
    { reportGoalId: goal.id },
    {
      queryKey: ["ReassessmentShortTermGoalPanel", goal.id],
      enabled: !!goal.id && !goal.id.startsWith("draft-"),
      refetchOnWindowFocus: false,
      refetchInterval: (query) => {
        const g = query.state.data?.getReportGoal;
        if (!g) return false;
        const isPending =
          g.aiProgressSummaryPredictionStatus ===
            ApiReportGoalAiProgressSummaryPredictionStatusChoices.GoalPredictionPending ||
          g.aiBaselineDataPredictionStatus ===
            ApiReportGoalAiBaselineDataPredictionStatusChoices.GoalPredictionPending ||
          g.aiGoalAttainmentScalePredictionStatus ===
            ApiReportGoalAiGoalAttainmentScalePredictionStatusChoices.GoalPredictionPending;
        return isPending ? config.REPORT_POLLING_INTERVAL : false;
      },
    },
  );

  const goalData = goalPanelQuery.data?.getReportGoal || undefined;

  const [isMedicalNecessityChecked, setIsMedicalNecessityChecked] = useState(goalData?.hasMedicalNecessity || false);
  const [selectedCriteria, setSelectedCriteria] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (goalData) {
      setProgressSummary(goalData?.progressSummary || "");
    }
  }, [goalData]);

  useEffect(() => {
    if (goal?.aiGoalAttainmentScalePredictionStatus) {
      setIsLocalGasGenerating(false);
    }
  }, [goal?.id]);

  const invalidateGoalQuery = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["ReassessmentShortTermGoalPanel", goal.id],
      }),
      queryClient.invalidateQueries({
        queryKey: ["get-reassessment-goals", report?.id],
      }),
    ]);
  }, [queryClient, goal.id, report?.id]);

  const startGasWorkflowMutation = useStartGoalAttainmentScaleWorkflowMutation({});

  const updateGoalProgressSummaryMutation = useUpdateGoalProgressSummaryMutation({});
  const startGoalProgressSummaryWorkflowMutation = useStartGoalProgressSummaryWorkflowMutation();
  const baselineDataWorkflowMutation = useStartGoalBaselineDataWorkflowMutation();

  const isApproved = goalData?.summaryReviewed || false;
  const isFieldReadOnly = readOnly || isApproved;

  const gasStatus = goalData?.aiGoalAttainmentScalePredictionStatus;
  const isGasGenerating =
    isLocalGasGenerating ||
    gasStatus === ApiReportGoalAiGoalAttainmentScalePredictionStatusChoices.GoalPredictionPending;
  const isGasGenerated =
    gasStatus === ApiReportGoalAiGoalAttainmentScalePredictionStatusChoices.GoalPredictionPredicted;
  const isGasFailed = gasStatus === ApiReportGoalAiGoalAttainmentScalePredictionStatusChoices.Failure;

  const showGasMatchMessage =
    goalData?.gasMatchStatus === ApiReportGoalGasMatchStatusChoices.Mismatch ||
    goalData?.gasMatchStatus === ApiReportGoalGasMatchStatusChoices.NoMatch;
  const showGasGenerateButton =
    goalData?.gasMatchStatus === ApiReportGoalGasMatchStatusChoices.Pending && !isGasGenerated;

  let gasButtonLabel = "Generate Values";
  if (isGasGenerating) gasButtonLabel = "Generating...";
  else if (isGasGenerated) gasButtonLabel = "Regenerate Values";

  const gasMatchMessage = goalData?.gasMatchMessage;

  const aiReportTreatmentPlanGoalProgressSummaryPredictionStatus =
    report?.reportTreatmentPlan?.aiGoalProgressSummaryPredictionStatus || null;
  const aiGoalProgressSummaryPredictionStatus = goalData?.aiProgressSummaryPredictionStatus || null;
  const aiBaselineDataPredictionStatus = goalData?.aiBaselineDataPredictionStatus || null;

  // Based on the saved values, not the local edit state, so an unsaved keystroke
  // doesn't clear a failure indicator before the save lands.
  const baselineDataValue = goalData?.baselineData || "";
  const hasBaselineData = baselineDataValue.trim().length > 0;

  const { progressSummaryGenerating, progressSummaryFailed, baselineDataGenerating, baselineDataFailed } =
    getGoalFieldGenerationStatus(
      aiReportTreatmentPlanGoalProgressSummaryPredictionStatus,
      aiGoalProgressSummaryPredictionStatus,
      aiBaselineDataPredictionStatus,
    );

  // useLayoutEffect runs before child useEffect, so the chart's mixed-type auto-switch
  // (which is a useEffect) sees the persisted chartType, evaluates against it, and wins
  // when it dispatches setSelectedChart(Cumulative). With a plain useEffect here, both
  // would fire in the same tick — child first, then parent — and the parent's Progress
  // reset would clobber the auto-switch on a goal where chartType was never persisted.
  useLayoutEffect(() => {
    setProgressSummary(null);
    setSignedChartImageUrl(goal.signedChartImageUrl || "");
    setSelectedChartReady(false);

    const chartType = goal.chartType as ApiReportGoalChartTypeChoices;

    setSelectedChart({
      id: chartType,
      primary: chartLabelOverrides[chartType] ?? convertReadableString(chartType),
    });
  }, [goal.id]);

  useEffect(() => {
    const selectedGoal = goalData || goal;
    if (selectedGoal) {
      setIsMedicalNecessityChecked(selectedGoal.hasMedicalNecessity || false);
      if (selectedGoal.medicalNecessityCriteria) {
        const initialChecklist = convertStringToChecklist(
          selectedGoal.medicalNecessityCriteria,
          MEDICAL_NECESSITY_DSM5_CRITERIA,
        );
        setSelectedCriteria(initialChecklist);
      } else {
        setSelectedCriteria({});
      }
    }
  }, [goalData, goal]);

  const handleGenerateGasValues = async () => {
    if (!goalData?.id) return;

    try {
      setIsLocalGasGenerating(true);

      await startGasWorkflowMutation.mutateAsync({
        reportGoalId: goalData.id,
      });
      invalidateGoalQuery();
    } catch (error) {
      console.error("Error in handleGenerateGasValues:", error);
      notifyError("Failed to generate GAS values.");
      setIsLocalGasGenerating(false);
    }
  };

  const handleGoalMutationSuccess = () => {
    invalidateGoalQuery();
  };

  const { saveGoalFields, saveGoalCustomField } = useInlineTreatmentPlanEdit({
    goalId: goalData?.id || undefined,
    reportId: report?.id || "",
    domainId: reportDomain?.id || "",
    learnerId,
    onSuccess: () => {
      invalidateGoalQuery();
    },
  });

  const getGoalApprovalErrors = () =>
    getGoalApprovalErrorsForContext(goalData, { isGasEnabled, isCatalightTemplate, shouldShowRationale, goalFields });

  // Attempt-time blockers in priority order: an unready chart takes precedence
  // over domain/field validation since neither of those can be fixed until
  // the chart settles anyway.
  const getApprovalBlockers = (): string[] => {
    if (!selectedChartReady) return ["chart_not_ready"];
    if (!currentDomainId) return ["domain_missing"];
    return getGoalApprovalErrors().map((e) => e.fieldKey);
  };

  const buildRequiredFieldsMessage = (errors: Array<{ message: string }>): string => {
    const list = errors.map((e) => e.message);
    const joined = list.length === 1 ? list[0] : `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
    const capitalized = joined.charAt(0).toUpperCase() + joined.slice(1);
    return list.length === 1 ? `${capitalized} is required!` : `${capitalized} are required!`;
  };

  const handleApproveGoal = async () => {
    if (!goalData?.id) return;

    const attemptNumber = ++approvalAttemptRef.current;
    const validationErrors = getApprovalBlockers();
    captureGoalApproveAttempted(goalData.id, {
      attempt_number: attemptNumber,
      validation_errors: validationErrors,
      chart_ready: selectedChartReady,
      has_targets: (goal.reporttargetSet?.edges?.length ?? 0) > 0,
      domain_id: currentDomainId,
    });

    if (!selectedChartReady) {
      notifyError("Please wait for the chart to finish loading before approving.");
      return;
    }

    if (!currentDomainId) {
      notifyError("Please assign a domain to this goal before approving.");
      return;
    }

    const errors = getGoalApprovalErrors();
    if (errors.length > 0) {
      notifyError(buildRequiredFieldsMessage(errors));
      return;
    }

    let chartImageUri = "";
    const chartInstance = chartRef.current;
    if (chartInstance && !!chartInstance.getSeriesTotal().length) {
      try {
        const imageBlob = (await chartInstance.dataURI()) as { imgURI: string };
        chartImageUri = imageBlob.imgURI || "";
      } catch (error) {
        console.error("Failed to capture chart image:", error);
      }
    }

    await updateGoalProgressSummaryMutation.mutateAsync(
      {
        input: {
          goalId: goalData.id,
          isApproved: true,
          progressSummary: progressSummary ?? null,
          chartType: selectedChart.id as ApiReportGoalChartTypeChoices,
          chartFromDate:
            filterSessionDates?.startDate && !isAllTimeSentinel(filterSessionDates.startDate)
              ? format(new Date(filterSessionDates.startDate), DATE_FORMAT)
              : undefined,
          chartToDate: filterSessionDates?.endDate
            ? format(new Date(filterSessionDates.endDate), DATE_FORMAT)
            : undefined,
          chartImageUri,
        },
      },
      {
        onSuccess: async (response) => {
          if (!response.updateGoalProgressSummary?.status) {
            notifyError("Failed to approve goal.");
            return;
          }
          if (response.updateGoalProgressSummary.signedChartImageUrl) {
            setSignedChartImageUrl(response.updateGoalProgressSummary.signedChartImageUrl);
          }
          invalidateGoalQuery();
          // Show a success toast and auto-advance to the next unapproved item
          // when the toast finishes (via its onClose callback). Capture the
          // approved goal id and read the navigation through the ref so a
          // manual sidebar navigation during the toast window can short-
          // circuit the advance in ProgrammingPage.
          const approvedItemId = goalData?.id;
          if (approvedItemId) {
            captureGoalApproved(approvedItemId, currentDomainId, attemptNumber);
            approvalAttemptRef.current = 0;
            notifySuccess("Goal approved.", {
              onClose: () => navigationRef.current?.onAdvanceAfterApprove?.(approvedItemId),
            });
          }
        },
        onError: async () => {
          notifyError("Failed to approve goal.");
        },
      },
    );
  };

  const handleRevokeGoal = async () => {
    if (!goalData?.id) return;
    setSignedChartImageUrl("");

    await updateGoalProgressSummaryMutation.mutateAsync(
      {
        input: {
          goalId: goalData.id,
          isApproved: false,
          progressSummary: progressSummary ?? null,
          chartType: selectedChart.id as ApiReportGoalChartTypeChoices,
          chartImageUri: "",
        },
      },
      {
        onSuccess: async (response) => {
          if (!response.updateGoalProgressSummary?.status) {
            notifyError("Failed to revoke approval.");
            return;
          }
          captureGoalApprovalRevoked(goalData.id, currentDomainId);
          approvalAttemptRef.current = 0;
          invalidateGoalQuery();
        },
        onError: async () => {
          notifyError("Failed to revoke approval.");
        },
      },
    );
  };

  const handleRegenerateSummary = () => {
    startGoalProgressSummaryWorkflowMutation.mutateAsync(
      {
        reportId: report?.id || "",
        reportGoalId: goalData?.id || goal.id,
      },
      {
        onSuccess(response) {
          if (response.startGoalProgressSummaryWorkflow?.status) {
            invalidateGoalQuery();
          } else {
            notifyError("Failed to regenerate goal progress summary.");
          }
        },
        onError() {
          notifyError("Failed to regenerate goal progress summary.");
        },
      },
    );
  };

  const handleRegenerateBaselineData = () => {
    baselineDataWorkflowMutation.mutateAsync(
      {
        reportId: report?.id || "",
        reportGoalId: goalData?.id || goal.id,
      },
      {
        onSuccess(response) {
          if (response.startGoalBaselineDataWorkflow?.status) {
            invalidateGoalQuery();
          } else {
            notifyError("Failed to regenerate goal baseline data.");
          }
        },
        onError() {
          notifyError("Failed to regenerate goal baseline data.");
        },
      },
    );
  };

  const masteryCriteriaValue = goalData?.masteryCriteria || "";
  const hasMasteryCriteria = masteryCriteriaValue.trim().length > 0;
  const canGenerateGas = hasBaselineData && hasMasteryCriteria;
  const status = goalData?.status ? statusMap[goalData.status] : null;
  const shouldShowRationale = status === CatalightGoalStatus.OnHold || status === CatalightGoalStatus.Discontinued;

  const saveProgressSummary = async (
    payload: Partial<ReportGoalInput>,
  ): Promise<{ success: boolean; error?: string }> => {
    if ("progressSummary" in payload) {
      setProgressSummary(payload.progressSummary || null);
    }
    return await saveGoalFields(payload);
  };

  const renderGasSection = () => {
    if (!isGasEnabled) return null;

    return (
      <div className="border-b border-limestone-100 -mx-8 px-8 my-6 py-6">
        <Label size="small" colorType="secondary" className="mb-4">
          Goal Attainment Scale
        </Label>

        <Paragraph size="extra-small" colorType="secondary" className="mb-4">
          Define each GAS level for this goal. You can either complete all five levels manually or enter the Baseline
          (0) and Target Treatment (2) levels and let us generate the remaining levels for you. The Generate Values
          button will activate once Levels 0 and 2 are filled in.
        </Paragraph>

        <GasPanel
          key={goalData?.id || Date.now()}
          GASData={{
            baselineData: goalData?.baselineData || "",
            masteryCriteria: goalData?.masteryCriteria || "",
            gasLevel1: goalData?.gasLevel1 || "",
            gasLevel3: goalData?.gasLevel3 || "",
            gasLevel4: goalData?.gasLevel4 || "",
          }}
          goalId={goalData?.id || undefined}
          reportId={report?.id || "<missing-report-id>"}
          domainId={reportDomain?.id || null}
          learnerId={learnerId || ""}
          onSubmitCallback={handleGoalMutationSuccess}
          goalName={goalData?.goalName || ""}
          isSidePanel={true}
          readOnly={isFieldReadOnly}
        />

        <div className="flex flex-row justify-between items-center mt-4">
          {showGasMatchMessage && (
            <Small className="flex flex-row items-center text-red-500!">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 inline mr-2" />
              {gasMatchMessage}
            </Small>
          )}
          {isGasFailed && (
            <Small className="flex flex-row items-center text-red-500!">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 inline mr-2" />
              Failed to Generate. Please try again.
            </Small>
          )}

          {showGasGenerateButton && !isFieldReadOnly && (
            <Tooltip
              text="Generating values first requires Levels 0 and 2. Add both to enable this button."
              showInfoIcon={false}
              infoIconClassName="h-3 w-3"
              disabled={isGasGenerating || isGasGenerated || canGenerateGas}
            >
              <span className="inline-block">
                <Button
                  data-testid="gas-generate-button"
                  text={
                    <div className="flex items-center">
                      <SparklesIcon className="w-5 h-5" /> &nbsp;
                      {gasButtonLabel}
                    </div>
                  }
                  appearance="text"
                  className="text-green-700 hover:bg-white [&]:border-green-700! [&:hover]:border-green-700! focus:border-green-700! active:border-green-700!"
                  type="button"
                  style={{ fontWeight: 400 }}
                  disabled={!canGenerateGas || isGasGenerating}
                  onClick={handleGenerateGasValues}
                />
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  const renderCriteriaSection = () => {
    if (!goalData) return null;

    return (
      <div>
        <TreatmentPlanForm
          templateFields={goalFields}
          nativeFieldValues={goalToFieldValues(goalData)}
          onNativeFieldSave={saveGoalFields}
          customFields={unwrapConnection(goalData.customFields) ?? []}
          onCustomFieldSave={saveGoalCustomField}
          onImageUploaded={invalidateGoalQuery}
          isReportCompleted={isFieldReadOnly}
          isReadOnly={isFieldReadOnly}
          domain={goal.reportDomain ?? null}
          fieldOverrides={{
            goal_name: null,
            status: null,
            gas_level_1: null,
            gas_level_3: null,
            gas_level_4: null,
            ...(isGasEnabled
              ? {
                  baseline_data: null,
                  mastery_criteria: null,
                  progress_summary: (
                    <InlineEditFieldWithAI
                      value={progressSummary || ""}
                      fieldKey="progressSummary"
                      onSave={saveProgressSummary}
                      title="Progress Summary"
                      isMultiline
                      isReportCompleted={isFieldReadOnly}
                      isGenerating={progressSummaryGenerating}
                      isGenerated={!!goalData.progressSummary}
                      isError={progressSummaryFailed}
                      errorMessage="AI progress summary generation failed"
                      onRegenerate={handleRegenerateSummary}
                      subTitle="How this goal has progressed over the last authorization period"
                      canRegenerate={!isApproved && !readOnly}
                    />
                  ),
                }
              : {
                  baseline_data: (
                    <InlineEditFieldWithAI
                      value={goalData.baselineData || ""}
                      fieldKey="baselineData"
                      onSave={saveGoalFields}
                      title="Baseline Data"
                      isMultiline
                      required
                      isReportCompleted={isFieldReadOnly}
                      isGenerating={baselineDataGenerating}
                      isGenerated={!!goalData.baselineData}
                      isError={baselineDataFailed}
                      errorMessage="AI baseline data generation failed"
                      onRegenerate={handleRegenerateBaselineData}
                      canRegenerate={!isApproved && !readOnly}
                    />
                  ),
                  progress_summary: (
                    <InlineEditFieldWithAI
                      value={progressSummary || ""}
                      fieldKey="progressSummary"
                      onSave={saveProgressSummary}
                      title="Progress Summary"
                      isMultiline
                      isReportCompleted={isFieldReadOnly}
                      isGenerating={progressSummaryGenerating}
                      isGenerated={!!goalData.progressSummary}
                      isError={progressSummaryFailed}
                      errorMessage="AI progress summary generation failed"
                      onRegenerate={handleRegenerateSummary}
                      subTitle="How this goal has progressed over the last authorization period"
                      canRegenerate={!isApproved && !readOnly}
                    />
                  ),
                }),
          }}
        />
      </div>
    );
  };

  const handleMedicalNecessityToggle = async (checked: boolean) => {
    setIsMedicalNecessityChecked(checked);
    await saveGoalFields({ hasMedicalNecessity: checked });
  };

  const handleCriteriaChange = async (itemId: string, checked: boolean) => {
    const updatedCriteria = {
      ...selectedCriteria,
      [itemId]: checked,
    };
    setSelectedCriteria(updatedCriteria);

    const criteriaString = convertChecklistToString(updatedCriteria, MEDICAL_NECESSITY_DSM5_CRITERIA);
    await saveGoalFields({ medicalNecessityCriteria: criteriaString, hasMedicalNecessity: isMedicalNecessityChecked });
  };
  const renderMedicalNecessitySection = () => {
    if (!isMedicalNecessityEnabled) return null;

    return (
      <div className="mb-6">
        <Small displayType="normal" colorType="secondary">
          Medical Necessity
        </Small>
        <label className="flex items-center gap-2 cursor-pointer py-2">
          <input
            type="checkbox"
            checked={isMedicalNecessityChecked}
            onChange={(e) => handleMedicalNecessityToggle(e.target.checked)}
            className="h-4 w-4 rounded-sm border-gray-300 text-interactive-primary-default focus:ring-border-focus"
            disabled={isFieldReadOnly}
          />
          <Small className="text-sm font-medium text-text-primary">Include Medical Necessity</Small>
        </label>

        {isMedicalNecessityChecked && (
          <div className="border rounded-lg p-4 bg-white space-y-6 mt-2">
            {MEDICAL_NECESSITY_DSM5_CRITERIA.map((section, index) => {
              const checklistItems = section.checklistItems.map((item, itemIndex) => {
                const isChecked = selectedCriteria[item.id] || false;
                return {
                  id: item.id,
                  title: `${itemIndex + 1}. ${item.title}`,
                  value: item.id,
                  checked: isChecked,
                  defaultChecked: isChecked,
                };
              });

              return (
                <div key={section.name}>
                  <Small colorType="primary">{`${String.fromCharCode(65 + index)}. ${section.name}`}</Small>
                  <div className="ml-6">
                    <CheckboxList
                      items={checklistItems}
                      title=""
                      legend={section.name}
                      orientation="vertical"
                      disabled={isFieldReadOnly}
                      inputClassName="disabled:checked:border-interactive-primary-default disabled:checked:bg-interactive-primary-default disabled:checked:hover:bg-interactive-primary-default"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const itemId = e.target.value;
                        handleCriteriaChange(itemId, e.target.checked);
                      }}
                      formNoValidate
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (goalPanelQuery.isLoading) {
    return <InlineEditorSkeleton />;
  }

  return (
    <div className="flex h-full flex-col">
      {isApproved && <ApprovalBanner approvedAt={goalData?.summaryReviewedOn} onRevoke={handleRevokeGoal} />}
      <div className="flex-1 overflow-y-auto">
        <div key={goalData?.id ?? "empty"} className="mx-auto max-w-4xl py-8 animate-editor-content-in">
          <div className="flex min-w-0 flex-1 flex-col rounded-2xl border border-limestone-100 bg-white">
            <div className="flex flex-col gap-2 border-b border-limestone-100 px-8 py-6">
              <div className="flex items-center gap-2">
                <div className="flex min-w-0 flex-1 items-end">
                  <InlineEditField
                    value={goalData?.goalName || ""}
                    fieldKey="goalName"
                    onSave={saveGoalFields}
                    title=""
                    required
                    isReportCompleted={isFieldReadOnly}
                    valueVariant="headline"
                  />
                </div>
                <GoalDomainTag
                  reportId={report?.id ?? ""}
                  domainId={currentDomainId}
                  onDomainChange={handleDomainChange}
                  isReadOnly={isFieldReadOnly}
                  isLoading={reassignDomainMutation.isPending}
                />
                <GoalBadge
                  status={status}
                  expanded
                  onStatusChange={(status) => saveGoalFields({ status: status as CatalightGoalStatus })}
                  statusOptions={CATALIGHT_GOAL_STATUS_SELECT_OPTIONS}
                  isReadOnly={isFieldReadOnly}
                />
              </div>
              {renderGasSection()}
              {renderCriteriaSection()}
              {renderMedicalNecessitySection()}
            </div>
            {goalData && (
              <div className="flex flex-col items-start overflow-hidden">
                <div className="w-full px-8 py-4">
                  <ShortTermGoalProgressGraph
                    key={goalData.id}
                    reportGoal={goalData}
                    selectedChart={selectedChart}
                    setSelectedChart={setSelectedChart}
                    signedChartImageUrl={signedChartImageUrl}
                    chartRef={chartRef}
                    filterSessionDates={filterSessionDates}
                    setFilterSessionDates={setFilterSessionDates}
                    onSelectedChartReady={setSelectedChartReady}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Floating Action Bar — must sit OUTSIDE the keyed/animated div above.
            `animate-editor-content-in` applies `transform`, which per CSS spec
            makes the transformed element a containing block for any
            `position: fixed` descendant. With the bar inside, it would anchor
            to the animated div and visibly jump during the 180ms transition,
            and also inherit the parent's opacity fade. Keeping it as a sibling
            of the animated div anchors it to the viewport instead. */}
        {goalData && !readOnly && (
          <ApprovalActionBar
            isApproved={isApproved}
            isPending={updateGoalProgressSummaryMutation.isPending}
            isApproveWaiting={!isApproved && !selectedChartReady}
            onApprove={handleApproveGoal}
            onRevoke={handleRevokeGoal}
            onPreviousGoal={navigation?.onPreviousGoal}
            onNextGoal={navigation?.onNextGoal}
            onPreviousTarget={navigation?.onPreviousTarget}
            onNextTarget={navigation?.onNextTarget}
            showTargetNavigation={isAddTargetsEnabled}
          />
        )}
      </div>
    </div>
  );
};
